import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { usePushBackHandler } from '../../context/BackStackContext'

type BottomSheetProps = {
  isOpen: boolean
  onClose: () => void
  /** Optional title displayed in the sheet header. */
  title?: string
  /** Max height of the sheet as a viewport percentage. Default: 88. */
  maxHeightSvh?: number
  /** Whether the user can drag the sheet down to dismiss. Default: true. */
  dismissible?: boolean
  className?: string
  footer?: ReactNode
  children: ReactNode
}

const DRAG_DISMISS_THRESHOLD_PX = 120
const DRAG_DISMISS_VELOCITY = 0.6

/**
 * Mobile bottom-sheet wrapper. Slides up from the bottom edge, includes a drag
 * handle for swipe-down dismissal, a tappable scrim, scroll lock, focus
 * management, and Esc-key dismiss. Renders into document.body via portal so it
 * is unaffected by ancestor `overflow` / `transform` containing blocks.
 */
export const BottomSheet = ({
  isOpen,
  onClose,
  title,
  maxHeightSvh = 88,
  dismissible = true,
  className,
  footer,
  children,
}: BottomSheetProps) => {
  usePushBackHandler(onClose, isOpen)
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const lastFocusRef = useRef<HTMLElement | null>(null)
  const [dragY, setDragY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen)
    if (isOpen) setDragY(0)
  }
  const dragStartRef = useRef<{ y: number; t: number } | null>(null)

  // Scroll lock + focus management.
  useEffect(() => {
    if (!isOpen) return
    lastFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    // Move focus into the sheet once it has mounted without auto-focusing typing fields.
    const focusTimer = window.setTimeout(() => {
      const explicitTarget = sheetRef.current?.querySelector<HTMLElement>(
        '[data-autofocus]:not([disabled]):not([data-autofocus="false"])',
      )
      if (explicitTarget) {
        explicitTarget.focus()
      } else {
        sheetRef.current?.focus()
      }
    }, 60)
    return () => {
      window.clearTimeout(focusTimer)
      document.body.style.overflow = previousOverflow
      lastFocusRef.current?.focus?.()
    }
  }, [isOpen])

  // Esc to close.
  useEffect(() => {
    if (!isOpen || !dismissible) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, dismissible, onClose])

  if (!isOpen) return null

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dismissible) return
    dragStartRef.current = { y: event.clientY, t: performance.now() }
    setIsDragging(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current) return
    const delta = event.clientY - dragStartRef.current.y
    setDragY(Math.max(0, delta))
  }

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current) return
    const dy = event.clientY - dragStartRef.current.y
    const dt = Math.max(1, performance.now() - dragStartRef.current.t)
    const velocity = dy / dt
    dragStartRef.current = null
    setIsDragging(false)
    if (dy > DRAG_DISMISS_THRESHOLD_PX || velocity > DRAG_DISMISS_VELOCITY) {
      onClose()
    } else {
      setDragY(0)
    }
  }

  const sheet = (
    <div
      className="bottomSheetScrim"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && dismissible) onClose()
      }}
    >
      <div
        ref={sheetRef}
        className={`bottomSheet ${className ?? ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title ?? 'Bottom sheet'}
        tabIndex={-1}
        style={{
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          maxHeight: `${maxHeightSvh}svh`,
          transition: isDragging ? 'none' : undefined,
        }}
      >
        {dismissible && (
          <div
            className="bottomSheetHandle"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            role="button"
            tabIndex={-1}
            aria-label="Drag to dismiss"
          >
            <span className="bottomSheetHandleBar" />
          </div>
        )}
        {title && <header className="bottomSheetHeader">{title}</header>}
        <div className="bottomSheetBody">{children}</div>
        {footer ? <div className="bottomSheetFooter">{footer}</div> : null}
      </div>
    </div>
  )

  return createPortal(sheet, document.body)
}
